import { useEffect, useState } from "react";
import { Crown, Sparkles, Zap } from "lucide-react";
import api from "../lib/api";
import { Button } from "./ui/button";

/**
 * SubscriptionBadge — small pill for Navbar / profile chips.
 * Reads /api/subscription/me once on mount and caches locally.
 */
export function SubscriptionBadge({ compact = false, onClick }) {
  const [sub, setSub] = useState(null);
  useEffect(() => {
    api.get("/subscription/me").then((r) => setSub(r.data)).catch(() => setSub(null));
  }, []);
  if (!sub) return null;
  const isPro = sub.is_pro;
  const cls = isPro
    ? "bg-gradient-to-r from-nexus-pink/20 via-nexus-purple/20 to-nexus-blue/20 border-nexus-pink/40 text-white"
    : "bg-white/5 border-white/15 text-zinc-300";
  const Icon = isPro ? Crown : Sparkles;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="subscription-badge"
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest transition-colors ${cls} ${onClick ? "hover:opacity-90 cursor-pointer" : "cursor-default"}`}
      aria-label={`Subscription: ${sub.label}`}
    >
      <Icon className="h-3 w-3" />
      {compact ? sub.label : sub.label}
    </button>
  );
}

/**
 * SubscriptionTierCard — full-size card for Wallet / Settings.
 * Shows current tier, price, benefits and an Upgrade CTA if not Pro.
 */
export function SubscriptionTierCard({ onUpgrade }) {
  const [sub, setSub] = useState(null);
  useEffect(() => {
    api.get("/subscription/me").then((r) => setSub(r.data)).catch(() => setSub(null));
  }, []);
  if (!sub) {
    return (
      <div className="glass rounded-2xl p-5 animate-pulse h-[92px]" data-testid="wallet-subscription-loading" />
    );
  }
  const isPro = sub.is_pro;
  const promoUntil = sub.pro_until ? new Date(sub.pro_until) : null;
  const daysRemaining = promoUntil ? Math.max(0, Math.round((promoUntil.getTime() - Date.now()) / 86400000)) : null;
  const bg = isPro
    ? "bg-gradient-to-br from-nexus-pink/15 via-nexus-purple/10 to-nexus-blue/15 border-nexus-pink/30"
    : "bg-white/[0.02] border-white/10";
  const Icon = isPro ? Crown : Zap;
  const iconTint = isPro ? "text-nexus-pink" : "text-zinc-300";
  const priceLabel = sub.promo_code
    ? `Promo · ${sub.promo_code}`
    : (sub.price_monthly > 0 ? `£${sub.price_monthly}/mo` : (sub.source === "owner" ? "Owner · comped" : "Free"));
  return (
    <div className={`rounded-2xl p-5 border ${bg} flex items-center justify-between gap-4`} data-testid="wallet-subscription-card">
      <div className="flex items-center gap-4 min-w-0">
        <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${isPro ? "bg-nexus-pink/20 border border-nexus-pink/40" : "bg-white/5 border border-white/10"}`}>
          <Icon className={`h-5 w-5 ${iconTint}`} />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-sm font-semibold" data-testid="subscription-tier-label">{sub.label}</div>
            {isPro && (
              <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-nexus-pink/20 border border-nexus-pink/40 text-nexus-pink font-bold">Pro</span>
            )}
          </div>
          <div className="text-xs text-zinc-400 mt-0.5 truncate">
            {isPro
              ? (sub.promo_code
                  ? `Promo Pro · ${daysRemaining} days left${promoUntil ? ` (until ${promoUntil.toLocaleDateString("en-GB", { day: "numeric", month: "short" })})` : ""}`
                  : (sub.source === "owner" ? "All features unlocked · comped for platform owners"
                                            : "All features unlocked · watermark-free Studio · priority support"))
              : "Upgrade to unlock the Studio, remove watermarks & get priority payouts"}
          </div>
          <div className="text-[11px] text-zinc-500 mt-1">{priceLabel}</div>
        </div>
      </div>
      {!isPro && onUpgrade && (
        <Button
          onClick={onUpgrade}
          className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90 shrink-0"
          data-testid="wallet-upgrade-btn"
        >
          Upgrade
        </Button>
      )}
    </div>
  );
}
